import { FileText } from 'lucide-react';
import { StatusChip, type StatusChipTone } from '@/components/ui/status-chip';
import { WorkspaceButton, WorkspaceSelect } from '@/components/ui/workspace';
import type { ReportVersion } from '@/lib/api/vendor-intelligence-report';
import { formatDate } from '@/lib/utils/format';
import { PDF_STATE_LABEL, pdfState, type PdfState } from './report-logic';

const PDF_TONE: Record<PdfState, StatusChipTone> = {
  ready: 'healthy',
  rendering: 'ai',
  failed: 'critical',
  none: 'unset',
};

/** What the carousel shows: the draft being edited, or a published version read-only. */
export type ReportView = { kind: 'draft' } | { kind: 'version'; snapshotId: string };

export const DRAFT_VIEW: ReportView = { kind: 'draft' };

/**
 * The versions above the carousel (E24-design §9): a select between the
 * draft and each published version, newest first, and the PDF of the
 * version being shown. Open PDF opens the shared preview, which reads and
 * downloads through the authorized file routes (PR #359), and can render a
 * missing or failed PDF again.
 */
export function VersionPicker({
  draftVersion,
  versions,
  view,
  onView,
  onOpenPdf,
}: {
  /** The draft's version, or null when there is no draft. */
  draftVersion: number | null;
  versions: ReportVersion[];
  view: ReportView;
  onView: (view: ReportView) => void;
  onOpenPdf: (version: ReportVersion) => void;
}) {
  if (draftVersion === null && versions.length === 0) return null;
  const shown =
    view.kind === 'version' ? versions.find((v) => v.snapshot_id === view.snapshotId) : null;
  const state = shown ? pdfState(shown) : null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="report-versions">
      <label htmlFor="vi-report-view" className="text-meta text-ink-500">
        Showing
      </label>
      <WorkspaceSelect
        id="vi-report-view"
        value={view.kind === 'draft' ? 'draft' : view.snapshotId}
        onChange={(e) =>
          onView(
            e.target.value === 'draft'
              ? DRAFT_VIEW
              : { kind: 'version', snapshotId: e.target.value },
          )
        }
        className="h-[30px] text-body-sm"
      >
        {draftVersion !== null ? (
          <option value="draft">Draft · version {draftVersion}</option>
        ) : null}
        {versions.map((v) => (
          <option key={v.report_id} value={v.snapshot_id}>
            Version {v.version} · published {formatDate(v.published_at)}
            {v.published_by.name ? ` by ${v.published_by.name}` : ''}
          </option>
        ))}
      </WorkspaceSelect>
      {shown && state ? (
        <>
          <StatusChip tone={PDF_TONE[state]} size="sm">
            {PDF_STATE_LABEL[state]}
          </StatusChip>
          <WorkspaceButton onClick={() => onOpenPdf(shown)}>
            <FileText className="h-3.5 w-3.5" aria-hidden="true" />
            Open PDF
          </WorkspaceButton>
        </>
      ) : null}
    </div>
  );
}
